/**
 * Compare Homes model (#1713): the pure parts. No React, no DOM except the
 * storage and URL helpers, which fail soft.
 *
 * DATA RULE: every fact here is read from an `ExploreProperty` row: one the
 * explorer loaded, or (#1816, share-link restore only) one the provider read
 * back through the same listing endpoint. Nothing in this module issues a
 * request.
 */
import type { ExploreProperty, QuoteRateplan } from '../data'
import type { ExploreWidgetConfig, VisitorFilter } from '../config'
import { formatPrice, nightlyPrice } from '../../../common/property-card/card-parts'
import { cardNoDatesLabel, rateUnbookable } from '../../../common/property-card/no-dates-label'
import { COMPARE_LABELS, type CompareLabels } from './labels'

// -- the snapshot ---------------------------------------------------------------

/** What one picked home contributes to the tray and the table. JSON-safe. */
export interface CompareSnap {
  slug: string
  id: string
  title: string
  /** Raw (unproxied) first photo; the UI proxies it at the size it needs. */
  image: string
  sleeps: number | null
  bedrooms: number | null
  bathrooms: number | null
  location: string
  amenities: string[]
  currency: string | null
  /** The nightly "from" rate, or null when there is nothing honest to show. */
  from: number | null
  /** The card's "no dates available" wording, when the card would print it instead of a rate. */
  unavailable: string | null
  /** The total for the guest's dates, already formatted, with the search it was quoted for. */
  total: { value: string; note: string; ctx: string } | null
  /** The search this snapshot was taken for (dates and party). */
  ctx: string
}

export type QuoteForCompare = { rateplan: QuoteRateplan; nights: number } | null | undefined

export interface SnapContext {
  options: ExploreWidgetConfig
  visitorFilters: Partial<VisitorFilter>
  quote?: QuoteForCompare
  /** The resolved strings for the night and tax notes (#1816); English when absent. */
  labels?: CompareLabels
}

/** The search a dated total only means something next to. */
export function searchCtx(filters: Partial<VisitorFilter>): string {
  const f = filters as Record<string, unknown>
  return ['checkin', 'checkout', 'guests', 'adults', 'children'].map((k) => `${k}=${f[k] ?? ''}`).join('&')
}

/**
 * Amenity names from whatever the row carries: the explorer's `{id, name}[]`
 * term list, plain strings, or a comma-separated string. Trimmed, de-duplicated,
 * blanks dropped.
 */
export function amenityNames(raw: unknown): string[] {
  let list: unknown[] = []
  if (Array.isArray(raw)) list = raw
  else if (typeof raw === 'string') list = raw.split(',')
  const out: string[] = []
  for (const item of list) {
    const name = typeof item === 'string'
      ? item
      : item && typeof item === 'object' && typeof (item as { name?: unknown }).name === 'string'
        ? (item as { name: string }).name
        : ''
    const clean = name.trim()
    if (clean && !out.includes(clean)) out.push(clean)
  }
  return out
}

function positive(n: unknown): number | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null
}

function locationOf(property: ExploreProperty): string {
  const label = property.location?.label?.trim()
  if (label) return label
  const sub = property.subtitle
  const parts = [sub?.city, sub?.state].map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean)
  if (parts.length) return parts.join(', ')
  if (property.city?.name) return property.city.name
  return (property.locations ?? []).map((l) => l.name).filter(Boolean).join(' / ')
}

/**
 * The total the card prints for the guest's dates, formatted the way the card
 * formats it: the full-price quote (taxes deducted unless Full Price After Tax
 * is on) when one has landed, else Central's calendar-derived per-stay total.
 */
function datedTotal(property: ExploreProperty, ctx: SnapContext, search: string): CompareSnap['total'] {
  const money = (amount: number) => formatPrice(amount, property.currency)
  const { quote, options } = ctx
  const labels = ctx.labels ?? COMPARE_LABELS
  const nightsNote = (n: number) => `${n} ${n === 1 ? labels.nightOne : labels.nightOther}`
  if (quote && quote.rateplan && quote.nights > 0) {
    const afterTax = options.fullPriceAfterTax === true
    const total = quote.rateplan.total - (afterTax ? 0 : quote.rateplan.totalTaxes)
    if (Number.isFinite(total) && total > 0) {
      return { value: money(total), note: `${nightsNote(quote.nights)} · ${afterTax ? labels.inclTaxes : labels.exclTaxes}`, ctx: search }
    }
  }
  const dp = property.datesPrice
  if (dp && (dp.total ?? 0) > 0) {
    return { value: money(dp.total as number), note: `${nightsNote(dp.nights)} · ${labels.exclTaxesFees}`, ctx: search }
  }
  return null
}

/** One result row to a snapshot. The slug is the key; callers skip rows without one. */
export function snapProperty(property: ExploreProperty, ctx: SnapContext): CompareSnap {
  const { options, visitorFilters } = ctx
  const search = searchCtx(visitorFilters)
  const pd = options.priceDisplay
  // Both price modes hidden: the cards print no price, so the table must not either.
  const showPrice = !(pd.noDates === 'none' && pd.withDates === 'none')
  const isDateQuery = Boolean(visitorFilters.checkin && visitorFilters.checkout)

  const total = showPrice && isDateQuery ? datedTotal(property, ctx, search) : null
  let unavailable: string | null = null
  let from: number | null = null
  if (showPrice && !total) {
    // The card's own rule: a rate Central has called unbookable reads as the label, not the base rate.
    const label = rateUnbookable(property.lowestPrice) ? cardNoDatesLabel(options, property) : null
    if (label) unavailable = label
    else from = nightlyPrice(property)
  }

  const beds = property.features?.beds
  return {
    slug: property.slug ?? '',
    id: String(property.id),
    title: property.title ?? '',
    image: Array.isArray(property.images) && typeof property.images[0] === 'string' ? property.images[0] : '',
    sleeps: positive(property.features?.guests),
    bedrooms: typeof beds === 'number' && Number.isFinite(beds) && beds >= 0 ? beds : null,
    bathrooms: positive(property.features?.baths),
    location: locationOf(property),
    amenities: amenityNames(property.amenities),
    currency: typeof property.currency === 'string' ? property.currency : null,
    from,
    unavailable,
    total,
    ctx: search,
  }
}

/**
 * A stored or restored snapshot against the search the guest is on now. A dated
 * total only describes the search it was quoted for, so a snapshot whose search
 * changed falls back to the "from" rate it also carries.
 */
export function freshenSnap(snap: CompareSnap, search: string): CompareSnap {
  if (snap.ctx === search) return snap
  return { ...snap, total: null, ctx: search }
}

// -- rows -----------------------------------------------------------------------

export interface CompareCell {
  /** Printed text; empty prints a dash. */
  text: string
  note?: string
  /** Amenity rows: whether the home lists it. */
  yes?: boolean
  /** Equality key: a row is "same" when every cell shares it. */
  key: string
}

export interface CompareRow {
  key: string
  label: string
  group?: 'amenities'
  cells: CompareCell[]
  same: boolean
}

function allSame(cells: CompareCell[]): boolean {
  return cells.length > 1 && cells.every((c) => c.key === cells[0].key)
}

function number(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value <= 0) return ''
  return String(Math.round(value * 10) / 10)
}

function priceCell(snap: CompareSnap, labels: CompareLabels): CompareCell {
  if (snap.total) return { text: snap.total.value, note: snap.total.note, key: `t:${snap.total.value}` }
  if (snap.unavailable) return { text: snap.unavailable, key: `u:${snap.unavailable}` }
  if (snap.from !== null) return { text: labels.priceFrom(formatPrice(snap.from, snap.currency)), key: `f:${snap.from}` }
  return { text: '', key: '' }
}

/** Amenities every home lists sink below the ones that tell the homes apart. */
export function amenityUnion(snaps: CompareSnap[]): string[] {
  const counts = new Map<string, number>()
  for (const snap of snaps) for (const name of snap.amenities) counts.set(name, (counts.get(name) ?? 0) + 1)
  return Array.from(counts.keys()).sort((a, b) => {
    const aAll = (counts.get(a) as number) >= snaps.length ? 1 : 0
    const bAll = (counts.get(b) as number) >= snaps.length ? 1 : 0
    return aAll - bAll || (counts.get(b) as number) - (counts.get(a) as number) || a.localeCompare(b)
  })
}

/** The table rows for the picked homes, labelled with `labels` (English when absent). */
export function buildRows(snaps: CompareSnap[], labels: CompareLabels = COMPARE_LABELS): CompareRow[] {
  const rows: CompareRow[] = []

  const prices = snaps.map((s) => priceCell(s, labels))
  if (prices.some((c) => c.key !== '')) {
    const allDated = snaps.length > 0 && snaps.every((s) => s.total !== null)
    rows.push({
      key: 'price',
      label: allDated ? labels.priceDates : labels.price,
      cells: prices,
      same: allSame(prices),
    })
  }

  const numeric = (key: string, label: string, pick: (s: CompareSnap) => CompareCell) => {
    const cells = snaps.map(pick)
    rows.push({ key, label, cells, same: allSame(cells) })
  }
  const num = (value: number | null): CompareCell => {
    const text = number(value)
    return { text, key: text }
  }
  numeric('sleeps', labels.sleeps, (s) => num(s.sleeps))
  numeric('bedrooms', labels.bedrooms, (s) => (s.bedrooms === 0
    ? { text: labels.studio, key: 'studio' }
    : num(s.bedrooms)))
  numeric('bathrooms', labels.bathrooms, (s) => num(s.bathrooms))
  numeric('location', labels.location, (s) => ({ text: s.location, key: s.location }))

  for (const name of amenityUnion(snaps)) {
    const cells = snaps.map((s): CompareCell => {
      const has = s.amenities.includes(name)
      return { text: '', yes: has, key: has ? '1' : '0' }
    })
    rows.push({ key: `amenity:${name}`, label: name, group: 'amenities', cells, same: allSame(cells) })
  }
  return rows
}

// -- the share link ---------------------------------------------------------------

export const SHARE_KEY = 'compare'

const SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9_.~-]{0,199}$/

/** Slugs re-screened on the way in: shape, no duplicates, capped. */
export function cleanSlugs(list: unknown, max: number): string[] {
  const out: string[] = []
  for (const item of Array.isArray(list) ? list : []) {
    const slug = typeof item === 'string' ? item.trim() : ''
    if (SLUG_RE.test(slug) && !out.includes(slug) && out.length < max) out.push(slug)
  }
  return out
}

/** The slugs a `compare=` param carries, or null without one. */
export function parseShareSlugs(search: string): string[] | null {
  try {
    const raw = new URLSearchParams(search).get(SHARE_KEY)
    return raw === null ? null : raw.split(',')
  } catch {
    return null
  }
}

/**
 * `href` with `compare=<slug,slug>` set (or removed for null). The rest of the
 * search (dates, party, filters) stays as it is; commas stay readable.
 */
export function withShareParam(href: string, slugs: string[] | null): string {
  const url = new URL(href)
  if (slugs && slugs.length) url.searchParams.set(SHARE_KEY, slugs.join(','))
  else url.searchParams.delete(SHARE_KEY)
  url.search = url.search.replace(/%2C/gi, ',')
  return url.toString()
}

// -- sessionStorage -----------------------------------------------------------------

export const COMPARE_STORE_VERSION = 1

/** One key per feed, per tab: a guest who opens a home and comes back expects the same picks. */
export const compareStoreKey = (feedId: number | string) => `hr-compare-${feedId}`

export interface CompareStore {
  v: number
  selected: string[]
  snaps: Record<string, CompareSnap>
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage || null : null
  } catch {
    return null // blocked storage (privacy settings): the picks live for this load only
  }
}

const str = (v: unknown, max = 300) => (typeof v === 'string' ? v.slice(0, max) : '')
const numOrNull = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null)

/** http(s) or site-relative only: a stored value is re-screened before it reaches an <img>. */
function safeImage(v: unknown): string {
  const s = str(v, 600)
  if (!s || /[\u0000-\u001f\u007f\s]/.test(s)) return ''
  return /^https?:\/\//i.test(s) || /^\/(?![/\\])/.test(s) ? s : ''
}

/** A stored snapshot, re-shaped field by field. Anything unusable reads as absent. */
export function sanitizeSnap(raw: unknown, slug: string): CompareSnap | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (r.slug !== slug) return null
  const total = r.total && typeof r.total === 'object' ? (r.total as Record<string, unknown>) : null
  return {
    slug,
    id: str(r.id, 80),
    title: str(r.title, 200),
    image: safeImage(r.image),
    sleeps: numOrNull(r.sleeps),
    bedrooms: numOrNull(r.bedrooms),
    bathrooms: numOrNull(r.bathrooms),
    location: str(r.location, 200),
    amenities: amenityNames(r.amenities).slice(0, 200).map((a) => a.slice(0, 120)),
    currency: typeof r.currency === 'string' && /^[A-Za-z]{3}$/.test(r.currency) ? r.currency.toUpperCase() : null,
    from: numOrNull(r.from),
    unavailable: r.unavailable ? str(r.unavailable, 120) : null,
    total: total && typeof total.value === 'string'
      ? { value: str(total.value, 60), note: str(total.note, 120), ctx: str(total.ctx, 300) }
      : null,
    ctx: str(r.ctx, 300),
  }
}

export function readCompareStore(feedId: number | string): CompareStore | null {
  const store = storage()
  if (!store) return null
  try {
    const saved = JSON.parse(store.getItem(compareStoreKey(feedId)) || 'null')
    if (!saved || saved.v !== COMPARE_STORE_VERSION || !Array.isArray(saved.selected)) return null
    const selected: string[] = cleanSlugs(saved.selected, 8)
    const snaps: Record<string, CompareSnap> = {}
    for (const slug of selected) {
      const snap = sanitizeSnap(saved.snaps?.[slug], slug)
      if (snap) snaps[slug] = snap
    }
    return { v: COMPARE_STORE_VERSION, selected, snaps }
  } catch {
    return null
  }
}

export function writeCompareStore(feedId: number | string, selected: string[], snaps: Record<string, CompareSnap>): void {
  const store = storage()
  if (!store) return
  try {
    const kept: Record<string, CompareSnap> = {}
    for (const slug of selected) if (snaps[slug]) kept[slug] = snaps[slug]
    store.setItem(compareStoreKey(feedId), JSON.stringify({ v: COMPARE_STORE_VERSION, selected, snaps: kept }))
  } catch { /* quota or blocked: this load keeps its picks, the next starts clean */ }
}

export function clearCompareStore(feedId: number | string): void {
  const store = storage()
  if (!store) return
  try { store.removeItem(compareStoreKey(feedId)) } catch { /* nothing to clear */ }
}
